/**
 * Type definitions for the "Looks" design preset system.
 *
 * A Look bundles header/hero/footer variants plus a set of CSS-driven knobs
 * (radius, density, button shape, card style) and curated typography +
 * palette families. Tenants pick one Look via the admin ThemeCustomizer; the
 * wizard's AI step seeds one at site creation. Each knob can be individually
 * overridden via `theme_config.look.overrides` without abandoning the Look.
 *
 * The allowed value of every knob lives ONCE, in `KNOB_VALUES`; the TypeScript
 * unions are derived from it. `resolveLook` validates against the same table,
 * and the admin fine-tune selects iterate it, so a new variant is added in one
 * place and every `Record<Variant, …>` label map stops compiling until it is
 * covered.
 */

export type PresetId =
  | 'editorial_boutique'
  | 'cozy_boutique'
  | 'modern_minimal'
  | 'bold_marketplace'
  | 'hospitality_warm'
  | 'cultural_classic'
  | 'playful_pop'
  | 'tech_saas';

export const KNOB_VALUES = {
  header_variant: ['classic', 'centered_logo', 'transparent_overlay', 'mega_menu', 'minimal'],
  hero_variant: ['standard', 'split', 'fullscreen_media', 'centered_card', 'search_forward', 'none'],
  footer_variant: ['multi_column', 'compact', 'big_cta', 'minimal'],
  button_shape: ['rounded', 'pill', 'square', 'cut_corner'],
  card_style: ['elevated', 'bordered', 'flat', 'glass'],
  radius_scale: ['sharp', 'soft', 'round', 'pill'],
  density: ['compact', 'comfortable', 'spacious'],
  /**
   * Product card treatment on the shop grid, featured rails and search:
   *   standard  image, badges, category eyebrow, name, price, hover actions (the original card)
   *   minimal   image, name, price — no eyebrow, no badges, no hover actions (editorial grids)
   *   overlay   name + price laid over the bottom of the image on a scrim (photography-led grids)
   * Applied by CSS keyed on `body[data-look-product-card]`, like the other knobs.
   */
  product_card_variant: ['standard', 'minimal', 'overlay'],
  /**
   * Curated font pair (display + sans). Resolves to Google Font family names
   * in `seed.ts` `FONT_PAIRS`:
   *   classic_serif  Playfair Display + Inter
   *   editorial      DM Serif Display + DM Sans
   *   modern_sans    Manrope + Manrope
   *   cozy_warm      Fraunces + Inter
   *   bold_grotesk   Space Grotesk + Inter
   *   museum         Cormorant Garamond + Source Sans 3
   *   playful        Fredoka + Nunito
   *   tech           Space Grotesk + Inter (JetBrains Mono for accents)
   */
  font_pair_id: ['classic_serif', 'editorial', 'modern_sans', 'cozy_warm', 'bold_grotesk', 'museum', 'playful', 'tech'],
  /**
   * Curated palette family. Each family seeds a hue range for primary +
   * secondary + accent; concrete shades come from `seed.ts` `PALETTE_FAMILIES`
   * and the backend palette generator. This is the high-level "feel" knob:
   *   warm_earth       sage + brown + terracotta (LSG today)
   *   cool_slate       slate + stone + sky
   *   jewel            emerald + plum + gold
   *   monochrome       neutral + neutral + single accent
   *   pastel           soft pink + lavender + mint
   *   vibrant          saturated primary + saturated complement
   *   editorial_ivory  ivory + ink + single jewel accent
   *   gradient_tech    dark base + gradient accent (saas)
   */
  palette_family_id: ['warm_earth', 'cool_slate', 'jewel', 'monochrome', 'pastel', 'vibrant', 'editorial_ivory', 'gradient_tech'],
} as const;

export type LookKnob = keyof typeof KNOB_VALUES;

export type HeaderVariant = (typeof KNOB_VALUES.header_variant)[number];
export type HeroVariant = (typeof KNOB_VALUES.hero_variant)[number];
export type FooterVariant = (typeof KNOB_VALUES.footer_variant)[number];
export type ButtonShape = (typeof KNOB_VALUES.button_shape)[number];
export type CardStyle = (typeof KNOB_VALUES.card_style)[number];
export type RadiusScale = (typeof KNOB_VALUES.radius_scale)[number];
export type Density = (typeof KNOB_VALUES.density)[number];
export type ProductCardVariant = (typeof KNOB_VALUES.product_card_variant)[number];
export type FontPairId = (typeof KNOB_VALUES.font_pair_id)[number];
export type PaletteFamilyId = (typeof KNOB_VALUES.palette_family_id)[number];

/** Fully resolved Look bundle (preset + overrides flattened). */
export interface ResolvedLook {
  preset_id: PresetId;
  header_variant: HeaderVariant;
  hero_variant: HeroVariant;
  footer_variant: FooterVariant;
  button_shape: ButtonShape;
  card_style: CardStyle;
  radius_scale: RadiusScale;
  density: Density;
  product_card_variant: ProductCardVariant;
  font_pair_id: FontPairId;
  palette_family_id: PaletteFamilyId;
}

/** Subset of ResolvedLook a tenant can override on top of a preset. */
export interface LookOverrides {
  header_variant?: HeaderVariant;
  hero_variant?: HeroVariant;
  footer_variant?: FooterVariant;
  button_shape?: ButtonShape;
  card_style?: CardStyle;
  radius_scale?: RadiusScale;
  density?: Density;
  product_card_variant?: ProductCardVariant;
  font_pair_id?: FontPairId;
  palette_family_id?: PaletteFamilyId;
}

/** Shape persisted at `site_settings.theme_config.look`. */
export interface LookConfig {
  preset_id: PresetId;
  overrides?: LookOverrides;
}

/** Preset definition — defaults for every knob. */
export type LookPreset = Omit<ResolvedLook, 'preset_id'>;
